/**
 * SensorDashboard - SOLID Principles Compliant Main Dashboard Component
 * 
 * SOLID Principles: All five principles applied through composition
 * Purpose: Main orchestrator component that composes specialized hooks,
 * components, and services to create a maintainable sensor dashboard.
 * 
 * Responsibilities:
 * - Coordinate between specialized hooks and components
 * - Manage React lifecycle and effects
 * - Provide clean composition of SOLID-compliant parts
 * - Handle high-level dashboard state orchestration
 */

import React, { useMemo } from 'react';
import SensorChart from '../solid-chart/SensorChartRefactored';
import DataTable from '../DataTable';
import { useSensorData } from '../../../hooks/useSensorData';
import { filterBuoyData } from '../../../services/dataService';
import { exportToCSV } from '../../../utils/csv';

// SOLID-compliant hooks
import { useBuoySelection } from './hooks/useBuoySelection';
import { useBuoyAvailability } from './hooks/useBuoyAvailability';
import { useDashboardActions } from './hooks/useDashboardActions';

// SOLID-compliant components
import BuoyPanel from './components/BuoyPanel';
import MergedBuoyPanel from './components/MergedBuoyPanel';
import BuoyButton from './components/BuoyButton';
import ViewModeButton from './components/ViewModeButton';
import EmptyStatePanel from './components/EmptyStatePanel';

// SOLID-compliant services
import DiagnosticsService from './services/DiagnosticsService';

import styles from './SensorDashboard.module.css';

// Only show these buoys in the UI
const ALLOWED_BUOYS = ['CT2001'];

// Main dashboard component refactored with SOLID principles
const SensorDashboard = React.memo(function SensorDashboard() {
  // === SOLID-compliant hooks for separation of concerns ===
  const buoySelection = useBuoySelection(ALLOWED_BUOYS);
  const {
    selectedBuoys,
    selectedSensors,
    selectedFields,
    isMergeMode,
    canMerge,
    toggleBuoy,
    setSelectedSensors,
    setSelectedFields,
    handleToggleMerge
  } = buoySelection;

  const dashboardActions = useDashboardActions(selectedBuoys, null, selectedFields);
  const {
    viewMode,
    timeRange,
    timeStop,
    isCustomRange,
    customStartLocal,
    customEndLocal,
    setGraphView,
    setTableView,
    handleTimeRangeChange,
    handleCustomStartChange,
    handleCustomEndChange
  } = dashboardActions;

  const {
    availableBuoys,
    perBuoyAvailability,
    buoysLoading,
    buoysError
  } = useBuoyAvailability(timeRange, ALLOWED_BUOYS, timeStop);

  // Data - using the optimized useSensorData hook
  const { loading: isLoading, filtered: rawData } = useSensorData(
    selectedBuoys,
    selectedSensors,
    selectedFields,
    timeRange,
    timeStop
  );

  // Export handler with proper dependencies
  const handleExport = React.useCallback(() => {
    selectedBuoys.forEach(buoyId => {
      const buoyRows = filterBuoyData(rawData, buoyId);
      const fields = selectedFields[buoyId] || [];
      if (buoyRows.length && fields.length) {
        exportToCSV(buoyRows, fields, buoyId);
      }
    });
  }, [selectedBuoys, rawData, selectedFields]);

  // Diagnostics (for table/chart) - using SOLID service
  const flaggedRows = useMemo(() => DiagnosticsService.analyzeFlaggedRows(rawData), [rawData]);

  return (
    <div className="App-main">
      
      <main className="App-content">
        {/* Top Controls: time range + buoys in one row (no labels) */}
        <div className={styles.topControls}>
          <select
            value={isCustomRange ? 'custom' : timeRange}
            onChange={handleTimeRangeChange}
            className={styles.select}
            aria-label="Select time range"
          >
            <option value="-1h">Last Hour</option>
            <option value="-6h">Last 6 Hours</option>
            <option value="-12h">Last 12 Hours</option>
            <option value="-24h">Last 24 Hours</option>
            <option value="-7d">Last 7 Days</option>
            <option value="-30d">Last 30 Days</option>
            <option value="custom">Custom range…</option>
          </select>

          {isCustomRange && (
            <div className={styles.customRange}>
              <label className={styles.customRangeLabel}>
                <span>From</span>
                <input
                  type="datetime-local"
                  value={customStartLocal}
                  onChange={handleCustomStartChange}
                  className={styles.customRangeInput}
                  aria-label="Custom range start"
                />
              </label>
              <label className={styles.customRangeLabel}>
                <span>To</span>
                <input
                  type="datetime-local"
                  value={customEndLocal}
                  onChange={handleCustomEndChange}
                  className={styles.customRangeInput}
                  aria-label="Custom range end"
                />
              </label>
            </div>
          )}

          <div className={styles.buoys}>
            {buoysLoading ? (
              <div className={styles.loadingText}>Loading buoys...</div>
            ) : buoysError ? (
              <div className={styles.errorText}>{buoysError}</div>
            ) : (availableBuoys.length ? (
              availableBuoys.map(id => (
                <BuoyButton
                  key={id}
                  id={id}
                  isSelected={selectedBuoys.includes(id)}
                  onClick={() => toggleBuoy(id)}
                >
                  {id}
                </BuoyButton>
              ))
            ) : (
              <div className={styles.mutedText}>No buoys found</div>
            ))}
            
            {/* Merge button right next to buoy selectors */}
            {canMerge && (
              <button
                onClick={handleToggleMerge}
                className={`${styles.btn} ${isMergeMode ? styles.btnActive : ''}`}
                title={isMergeMode ? "Switch back to individual buoy view" : "Combine data from multiple buoys in one chart"}
              >
                {isMergeMode ? "Unmerge" : "Merge"}
              </button>
            )}
          </div>

          <div className={styles.viewRight}>
            {isLoading && <div className={styles.mutedText}>Fetching data…</div>}
            <ViewModeButton
              mode={viewMode}
              isActive={viewMode === 'graph'}
              onClick={setGraphView}
            >
              Graph View
            </ViewModeButton>
            <ViewModeButton
              mode={viewMode}
              isActive={viewMode === 'table'}
              onClick={setTableView}
            >
              Table View
            </ViewModeButton>
          </div>
        </div>

        {/* Merged view or individual buoy panels */}
        {isMergeMode && canMerge ? (
          <MergedBuoyPanel
            selectedBuoys={selectedBuoys}
            perBuoyAvailability={perBuoyAvailability}
            selectedSensors={selectedSensors}
            setSelectedSensors={setSelectedSensors}
            selectedFields={selectedFields}
            setSelectedFields={setSelectedFields}
            isLoading={isLoading}
            viewMode={viewMode}
            rawData={rawData}
            onToggleMerge={handleToggleMerge}
            canMerge={canMerge}
          />
        ) : (
          /* One panel per selected buoy */
          selectedBuoys.map(buoyId => {
            const { availableSensors = [], availableFieldsBySensor = {} } = perBuoyAvailability[buoyId] || {};
            // Filter data to this specific buoy for individual panels
            const buoyRows = filterBuoyData(rawData, buoyId);
            const buoyFlaggedRows = DiagnosticsService.getFlaggedRowsForBuoy(flaggedRows, buoyId);

            return (
              <BuoyPanel
                key={buoyId}
                buoyId={buoyId}
                availableSensors={availableSensors}
                availableFieldsBySensor={availableFieldsBySensor}
                selectedSensors={selectedSensors}
                setSelectedSensors={setSelectedSensors}
                selectedFields={selectedFields}
                setSelectedFields={setSelectedFields}
                isLoading={isLoading}
                viewMode={viewMode}
                buoyRows={buoyRows}
                flaggedRows={buoyFlaggedRows}
                onToggleMerge={handleToggleMerge}
                canMerge={canMerge}
                isMerged={isMergeMode}
              />
            );
          })
        )}

        {/* Empty state - Ocean Alk-Align Project Overview */}
        {selectedBuoys.length === 0 && (
          <EmptyStatePanel />
        )}

        {/* Export */}
        {selectedBuoys.length > 0 && (
          <div className={styles.exportBar}>
            <button
              onClick={handleExport}
              className={styles.exportBtn}
            >
              Export Data
            </button>
          </div>
        )}
      </main>
    </div>
  );
});

export default SensorDashboard;